import {
  ALL_EVENT_LEVELS,
  ALL_EVENT_ORGANIZERS,
  ALL_EVENT_TYPES,
  DATE_FILTERS,
} from "../data/convocationsAdmin.constants";

export const normalizeText = (value) =>
  (value ?? "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const toValidDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatEventDate = (value) => {
  const date = toValidDate(value);

  if (!date) {
    return "Date non renseignée";
  }

  return date.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatEventTime = (value) => {
  const date = toValidDate(value);

  if (!date) {
    return null;
  }

  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Récupère le type d'un événement en acceptant plusieurs
 * variantes de noms de propriétés possibles.
 */
export const getEventType = (event) => {
  const rawType =
    event?.type_event ??
    event?.typeEvent ??
    event?.event_type ??
    event?.type?.label ??
    event?.type?.nom ??
    "";

  return rawType.toString().trim();
};

export const getEventOrganizer = (event) => {
  const rawOrganizer =
    event?.organisateur ??
    event?.organizerEvent ??
    event?.event_organizer ??
    event?.organisateur_event ??
    event?.organisateurEvent ??
    event?.organisateur ??
    event?.organizer ??
    event?.organization?.name ??
    event?.organization?.nom ??
    event?.Organisateur?.nom ??
    event?.Organisateur?.name ??
    "";

  if (typeof rawOrganizer !== "string" && typeof rawOrganizer !== "number") {
    return "";
  }

  return rawOrganizer.toString().trim();
};

export const getEventLevel = (event) => {
  const rawLevel =
    event?.level_event ??
    event?.levelEvent ??
    event?.event_level ??
    event?.niveau_event ??
    event?.niveauEvent ??
    event?.level?.label ??
    event?.level?.nom ??
    "";

  if (typeof rawLevel !== "string" && typeof rawLevel !== "number") {
    return "";
  }

  return rawLevel.toString().trim();
};

const buildUniqueEventOptions = (events = [], valueGetter) => {
  const uniqueValues = new Map();

  events.forEach((event) => {
    const value = valueGetter(event);
    const normalizedValue = normalizeText(value);

    if (normalizedValue && !uniqueValues.has(normalizedValue)) {
      uniqueValues.set(normalizedValue, value);
    }
  });

  return Array.from(uniqueValues.values()).sort((valueA, valueB) =>
    valueA.localeCompare(valueB, "fr", {
      sensitivity: "base",
    }),
  );
};

export const buildEventOrganizerOptions = (events = []) =>
  buildUniqueEventOptions(events, getEventOrganizer);

export const buildEventLevelOptions = (events = []) =>
  buildUniqueEventOptions(events, getEventLevel);

export const getEventStatus = (start, now = new Date()) => {
  const date = toValidDate(start);

  if (!date) {
    return {
      key: "unknown",
      label: "Date inconnue",
      className:
        "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200",
    };
  }

  if (date.toDateString() === now.toDateString()) {
    return {
      key: "today",
      label: "Aujourd’hui",
      className:
        "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    };
  }

  if (date < now) {
    return {
      key: "past",
      label: "Passé",
      className:
        "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200",
    };
  }

  return {
    key: "upcoming",
    label: "À venir",
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
  };
};

const toCount = (value) => {
  const count = Number(value);

  return Number.isFinite(count) && count >= 0 ? count : null;
};

export const getConvocationCount = (event) => {
  if (!event) {
    return 0;
  }

  const countFields = [
    event.nombreConvocations,
    event.convocations_count,
    event.nb_convocations,
  ];

  for (const value of countFields) {
    const count = toCount(value);

    if (count !== null) {
      return count;
    }
  }

  const collectionFields = [
    event.ActeurEvents,
    event.acteurEvents,
    event.Actors,
    event.acteurs,
    event.Acteur,
  ];

  const collection = collectionFields.find(Array.isArray);

  return collection?.length ?? 0;
};

/**
 * Permet d'accepter soit un tableau direct, soit une réponse
 * contenant le tableau dans une propriété.
 */
export const extractCollection = (payload, keys = []) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

export const buildConvocationCountMap = (rows = []) =>
  rows.reduce((counts, row) => {
    const eventId =
      row?.event_id ??
      row?.eventId ??
      row?.EventId ??
      row?.Event?.id ??
      row?.event?.id;

    if (eventId === null || eventId === undefined) {
      return counts;
    }

    counts[eventId] = (counts[eventId] ?? 0) + 1;

    return counts;
  }, {});

export const enrichEventsWithConvocationCounts = (events, results) => {
  const countMap = buildConvocationCountMap(results);

  return events.map((event) => ({
    ...event,
    nombreConvocations: countMap[event.id] ?? getConvocationCount(event),
  }));
};

/**
 * Important :
 * les types sont récupérés depuis TOUS les événements.
 *
 * Ils ne proviennent pas uniquement des événements qui possèdent
 * déjà une convocation.
 */
export const buildEventTypeOptions = (events = []) => {
  const uniqueTypes = new Map();

  events.forEach((event) => {
    const type = getEventType(event);
    const normalizedType = normalizeText(type);

    if (normalizedType && !uniqueTypes.has(normalizedType)) {
      uniqueTypes.set(normalizedType, type);
    }
  });

  return Array.from(uniqueTypes.values()).sort((typeA, typeB) =>
    typeA.localeCompare(typeB, "fr", {
      sensitivity: "base",
    }),
  );
};

export const eventMatchesDateFilter = (event, dateFilter, now = new Date()) => {
  if (dateFilter === DATE_FILTERS.ALL) {
    return true;
  }

  const date = toValidDate(event?.start);

  if (!date) {
    return false;
  }

  if (dateFilter === DATE_FILTERS.TODAY) {
    return date.toDateString() === now.toDateString();
  }

  if (dateFilter === DATE_FILTERS.PAST) {
    return date < now;
  }

  if (dateFilter === DATE_FILTERS.UPCOMING) {
    return date >= now;
  }

  return true;
};

export const filterAndSortEvents = (
  events,
  { search, type, organizer, level, date, onlyWithConvocations },
) => {
  const normalizedSearch = normalizeText(search);
  const normalizedType = normalizeText(type);
  const normalizedOrganizer = normalizeText(organizer);
  const normalizedLevel = normalizeText(level);

  const now = new Date();

  return events
    .filter((event) => !onlyWithConvocations || getConvocationCount(event) > 0)
    .filter((event) => {
      if (!normalizedSearch) {
        return true;
      }

      const searchableContent = normalizeText(
        [
          event?.titre,
          event?.description,
          event?.lieu,
          event?.location,
          getEventType(event),
          getEventOrganizer(event),
          getEventLevel(event),
        ]
          .filter(Boolean)
          .join(" "),
      );

      return searchableContent.includes(normalizedSearch);
    })
    .filter(
      (event) =>
        type === ALL_EVENT_TYPES ||
        normalizeText(getEventType(event)) === normalizedType,
    )
    .filter(
      (event) =>
        organizer === ALL_EVENT_ORGANIZERS ||
        normalizeText(getEventOrganizer(event)) === normalizedOrganizer,
    )
    .filter(
      (event) =>
        level === ALL_EVENT_LEVELS ||
        normalizeText(getEventLevel(event)) === normalizedLevel,
    )
    .filter((event) => eventMatchesDateFilter(event, date, now))
    .sort((eventA, eventB) => {
      const dateA = toValidDate(eventA?.start);
      const dateB = toValidDate(eventB?.start);

      if (!dateA && !dateB) {
        return (eventA?.titre ?? "").localeCompare(eventB?.titre ?? "", "fr");
      }

      if (!dateA) {
        return 1;
      }

      if (!dateB) {
        return -1;
      }

      return dateA.getTime() - dateB.getTime();
    });
};

export const getConvocationsStats = (events = []) => {
  const now = new Date();

  return events.reduce(
    (stats, event) => {
      const start = toValidDate(event?.start);
      const convocationCount = getConvocationCount(event);

      stats.totalEvents += 1;
      stats.totalConvocations += convocationCount;

      if (convocationCount > 0) {
        stats.withConvocations += 1;
      }

      if (start && start >= now) {
        stats.upcoming += 1;
      }

      return stats;
    },
    {
      totalEvents: 0,
      upcoming: 0,
      withConvocations: 0,
      totalConvocations: 0,
    },
  );
};

export const getTypeBadgeClass = (type) => {
  const normalizedType = normalizeText(type);

  /*
   * Compétitions et passages de grade
   */
  if (
    ["competition", "passage de grade", "tournoi interclubs"].includes(
      normalizedType,
    )
  ) {
    return "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300";
  }

  /*
   * Stages, entraînements et regroupements sportifs
   */
  if (
    [
      "stage & regroupement technique",
      "entrainement departemental",
      "stage club",
      "stage sportif departemental",
      "stage sportif regional",
      "cours commun club",
    ].includes(normalizedType)
  ) {
    return "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300";
  }

  /*
   * Formations et séminaires
   */
  if (["formation", "formation snr", "seminaire"].includes(normalizedType)) {
    return "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300";
  }

  /*
   * Animations et événements culturels
   */
  if (["animation", "kagami biraki", "culture judo"].includes(normalizedType)) {
    return "border-purple-200 bg-purple-50 text-purple-800 dark:border-purple-500/20 dark:bg-purple-500/10 dark:text-purple-300";
  }

  /*
   * Réunions institutionnelles
   */
  if (["reunion", "assemblee generale"].includes(normalizedType)) {
    return "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300";
  }

  /*
   * Autre ou type inconnu
   */
  return "border-slate-200 bg-slate-100 text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200";
};

export const pluralize = (count, singular, plural = `${singular}s`) =>
  count > 1 ? plural : singular;
